import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Image,
  Text,
  useToast,
  useUpdateEffect,
} from '@chakra-ui/react';
import { Gender } from 'enums/shared/gender';
import { Form, Formik } from 'formik';
import useFormError from 'hooks/shared/useFormError';
import useToastError from 'hooks/shared/useToastError';
import { Profile } from 'model/modules/profile/profile';
import moment from 'moment';
import FormDateInput from 'presentation/components/shared/form/FormDateInput';
import FormPhoneNumberInput from 'presentation/components/shared/form/FormPhoneNumberInput';
import FormSelectInput from 'presentation/components/shared/form/FormSelectInput';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import FullScreenLoader from 'presentation/components/shared/FullScreenLoader';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadFileMutation } from 'rtkQuery/sharespro/queries/common';
import { useUpdateProfileMutation } from 'rtkQuery/sharespro/queries/profile';
import { useFilePicker } from 'use-file-picker';
import * as Yup from 'yup';
import styles from './styles.module.scss';
import profileSvg from 'assets/img/icons/profile.svg';
import classNames from 'classnames';

interface Props {
  data: Profile;
}

interface FormValues {
  first_name: string;
  last_name: string;
  second_name: string;
  phone: string;
  email: string;
  birth_day: Date | null;
  gender: Gender | null;
}

const ProfileLoaded: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const [updateProfile, { isLoading: isSubmitting }] =
    useUpdateProfileMutation();

  const toast = useToast();
  const handleFormError = useFormError<FormValues>();
  const handleToastError = useToastError();

  const [uploadFileToApi, { isLoading: isUploadingImage }] =
    useUploadFileMutation();

  const validationSchemaShape: Record<string, any> = {};
  if (data.email) {
    validationSchemaShape.email = Yup.string()
      .email(t('invalidEmail') ?? '')
      .required(t('fieldIsRequired') ?? '');
  } else {
    validationSchemaShape.email = Yup.string().email(t('invalidEmail') ?? '');
  }
  if (data.firstName) {
    validationSchemaShape.first_name = Yup.string().required(
      t('fieldIsRequired') ?? ''
    );
  }
  if (data.lastName) {
    validationSchemaShape.last_name = Yup.string().required(
      t('fieldIsRequired') ?? ''
    );
  }
  if (data.secondName) {
    validationSchemaShape.second_name = Yup.string().required(
      t('fieldIsRequired') ?? ''
    );
  }

  const validationSchema = Yup.object().shape(validationSchemaShape);

  const [
    openFilePicker,
    { plainFiles, errors: fileErrors, loading: pickingFile },
  ] = useFilePicker({
    multiple: false,
    accept: ['.png', '.jpg', '.jpeg'],
    maxFileSize: 10,
  });

  useUpdateEffect(() => {
    if (fileErrors.length > 0) {
      if (fileErrors[0].fileSizeToolarge) {
        toast({
          title: t('error'),
          description: t('fileTooBigMb', { size: 10 }),
          status: 'error',
          position: 'top-right',
          isClosable: true,
        });
      }
    }
  }, [fileErrors]);

  useUpdateEffect(() => {
    if (plainFiles.length > 0) {
      uploadFileToApi(plainFiles[0])
        .unwrap()
        .then((value) => {
          updateProfile({
            photo_id: value.data.id,
          })
            .unwrap()
            .then(() => {
              toast({
                description: t('profileUpdated'),
                status: 'success',
                position: 'top-right',
                isClosable: true,
              });
            })
            .catch(handleToastError);
        })
        .catch(handleToastError);
    }
  }, [plainFiles]);

  return (
    <>
      <Box pos="relative" mb="40px" className={styles.avatarWrap}>
        <Image
          src={data.avatar ?? profileSvg}
          w="150px"
          h="150px"
          pos="relative"
          borderRadius="50%"
        />
        {!!data.avatar && (
          <Box
            onClick={() => {
              updateProfile({
                photo_id: null,
              })
                .unwrap()
                .then(() => {
                  toast({
                    description: t('profileUpdated'),
                    status: 'success',
                    position: 'top-right',
                    isClosable: true,
                  });
                })
                .catch(handleToastError);
            }}
            className={styles.avatarDeleteBtn}
          >
            <CloseIcon />
          </Box>
        )}
        <Box
          className={classNames(styles.avatarEditOverlay, {
            [styles.avatarEditOverlay__withPhoto]: !data.avatar,
          })}
          onClick={() => {
            openFilePicker();
          }}
        >
          <Text
            maxW="100px"
            color={data.avatar ? '#fff' : '#000'}
            textAlign="center"
          >
            {data.avatar ? t('updatePhoto') : t('addProfilePhoto')}
          </Text>
        </Box>
      </Box>
      <Formik<FormValues>
        initialValues={{
          first_name: data.firstName ?? '',
          last_name: data.lastName ?? '',
          second_name: data.secondName ?? '',
          phone: data.phone ?? '',
          email: data.email ?? '',
          birth_day: data.birthday,
          gender: data.gender,
        }}
        onSubmit={(values, formik) => {
          updateProfile({
            first_name: values.first_name,
            last_name: values.last_name,
            second_name: values.second_name,
            email: values.email,
            birth_day: values.birth_day
              ? moment(values.birth_day).format('DD.MM.YYYY')
              : undefined,
            gender: values.gender ?? undefined,
          })
            .unwrap()
            .then(() => {
              toast({
                title: t('profileUpdated'),
                status: 'success',
                position: 'top-right',
                isClosable: true,
              });
            })
            .catch((err) => {
              handleFormError(err, formik);
            });
        }}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue, handleChange }) => (
          <Form>
            <Flex flexDirection="column">
              <Box mb="40px">
                <FormTextInput
                  name="first_name"
                  value={values.first_name}
                  onChange={handleChange}
                  label={t('firstNameLabel')}
                />
              </Box>
              <Box mb="40px">
                <FormTextInput
                  name="last_name"
                  value={values.last_name}
                  onChange={handleChange}
                  label={t('lastNameLabel')}
                />
              </Box>
              <Box mb="40px">
                <FormTextInput
                  name="second_name"
                  value={values.second_name}
                  onChange={handleChange}
                  label={t('middleNameLabel')}
                />
              </Box>
              <Box mb="40px">
                <FormPhoneNumberInput
                  value={values.phone}
                  onChange={handleChange}
                  name="phone"
                  label={t('phoneLabel')}
                  disabled
                />
                <Text mt="10px">{t('profilePhoneInputDescription')}</Text>
              </Box>
              <Box mb="40px">
                <FormTextInput
                  name="email"
                  type="email"
                  onChange={handleChange}
                  value={values.email}
                  label={t('emailLabel')}
                />
              </Box>
              <Box mb="40px">
                <FormDateInput
                  selected={values.birth_day}
                  name="birth_day"
                  onChange={(date) => {
                    setFieldValue('birth_day', date);
                  }}
                  label={t('birthdayLabel')}
                />
              </Box>
              <Box mb="40px">
                <FormSelectInput
                  onChange={handleChange}
                  name="gender"
                  label={t('genderLabel')}
                  value={values.gender ?? undefined}
                >
                  <option />
                  <option value={Gender.Male}>{t('selectGenderMale')}</option>
                  <option value={Gender.Female}>
                    {t('selectGenderFemale')}
                  </option>
                </FormSelectInput>
              </Box>
            </Flex>
            <Button type="submit" isLoading={isSubmitting}>
              {t('save')}
            </Button>
          </Form>
        )}
      </Formik>
      <FullScreenLoader isLoading={pickingFile || isUploadingImage} />
    </>
  );
};

export default ProfileLoaded;
